import React, { useState, useRef, useMemo, PropsWithChildren } from 'react';
import useId from '@mc/hooks/useId';
import TabContext from './TabContext';

export type TabsProps = PropsWithChildren<{
  /** Default index number. */
  defaultIndex?: number | string;
  /** Current index number. */
  index?: number;
  /** Tabs require manual selection, after focus on*/
  isManual?: boolean;
  /** Triggers when the Tab value has changed. */
  onChange?: VoidFunction;
  /** Option to render small or large tabs. */
  size?: 'large' | 'small';
}>;

const Tabs = function Tabs({
  size = 'large',
  children,
  index,
  defaultIndex,
  onChange,
  isManual = false,
}: TabsProps) {
  const { current: isControlled } = useRef(index !== undefined);
  const [uncontrolledIndex, setUncontrolledIndex] = useState(defaultIndex || 0);
  const [focusIndex, setFocusIndex] = useState(defaultIndex || 0);
  const [showOutline, setShowOutline] = useState(false);
  const id = useId();

  const currentIndex = isControlled ? index : uncontrolledIndex;
  const setCurrentIndex = isControlled ? onChange : setUncontrolledIndex;

  const contextProps = useMemo(
    () => ({
      id,
      currentIndex,
      setCurrentIndex,
      size,
      focusIndex,
      setFocusIndex,
      showOutline,
      setShowOutline,
      isManual,
    }),
    [
      id,
      currentIndex,
      setCurrentIndex,
      size,
      focusIndex,
      showOutline,
      isManual,
    ],
  );

  return (
    <TabContext.Provider value={contextProps}>{children}</TabContext.Provider>
  );
};

export default Tabs;
